import axios from "axios";
import { NotificationManager } from "react-notifications";
import { Auth } from "aws-amplify";

/**
 * The only API client. Every staff journey goes through the Core API; the
 * Legacy `common-{version}` and `admin-{version}` clients were removed with
 * their cutover flags in EPA-2583.
 */
export const coreApi = axios.create({
  baseURL: `${process.env.REACT_APP_NEW_API_URL}/v1`,
  withCredentials: false,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
    // Names this client in the Core API's logs, so its traffic can be told
    // apart from every other caller's (EPA-2584). Observability only: the Core
    // API never uses it to decide what a request is allowed to do.
    //
    // The Core API must allow this header before this client sends it. The
    // browser preflights every request here, and a server that does not list
    // the header in `Access-Control-Allow-Headers` makes every call fail.
    "X-EPA-Client": "current-cms",
  },
  timeout: 10000,
});

/**
 * Set on the error when there is no Cognito session to sign a request with.
 * Amplify rejects with a bare string then (`"No current user"`), which carries
 * no message and no status, so it is replaced with an error that says what
 * happened and that `handleError` can recognise.
 */
export const SESSION_EXPIRED = "Your session has expired. Please sign in again.";

/**
 * Whether a sign-out is already under way. A page fires several requests at
 * once, and when the session has gone every one of them fails the same way:
 * one notice and one redirect are enough.
 */
let signingOut = false;

coreApi.interceptors.request.use(async (config) => {
  let session;
  try {
    session = await Auth.currentSession();
  } catch (cause) {
    const error = new Error(SESSION_EXPIRED);
    error.sessionExpired = true;
    error.cause = cause;
    throw error;
  }
  // A session again means the staff member has signed back in, so the next
  // expiry must sign them out too.
  signingOut = false;
  config.headers.Authorization = `Bearer ${session.getIdToken().getJwtToken()}`;
  return config;
});

/**
 * The Core API's id for a failed request, when it sent one back.
 *
 * Shown on the error so a staff member can quote it, and the exact request can
 * be found in the service logs. The Core API generates it; this client never
 * does. Absent when the request never reached the server, or before the Core
 * API exposed the header.
 */
const requestIdOf = (error) =>
  (error.response &&
    error.response.headers &&
    error.response.headers["x-request-id"]) ||
  null;

export const isSessionExpired = (error) =>
  Boolean(
    error &&
      (error.sessionExpired ||
        (error.response && error.response.status === 401))
  );

/**
 * What a failure is logged as. Never the error itself: axios keeps the request
 * config on it, and that carries the staff member's token in `Authorization`.
 */
const describeFailure = (error) => ({
  message: error ? error.message : String(error),
  status: error && error.response ? error.response.status : undefined,
  method: error && error.config ? error.config.method : undefined,
  url: error && error.config ? error.config.url : undefined,
  requestId: error ? requestIdOf(error) : null,
});

export const handleError = ({ error, history }) => {
  console.error("network error: ", describeFailure(error));
  // A signed-out staff member can do nothing on this page, so they are sent to
  // sign in again rather than left with an error on every action.
  if (isSessionExpired(error)) {
    if (signingOut) {
      return;
    }
    // A 401 can also be a suspended or deleted account; the server says which.
    const reason =
      error.response && error.response.data && error.response.data.message;
    NotificationManager.error(
      reason ? `You have been signed out: ${reason}` : SESSION_EXPIRED
    );
    if (history) {
      signingOut = true;
      history.push("/logout");
    }
    return;
  }
  const message =
    (error &&
      error.response &&
      error.response.data &&
      error.response.data.message) ||
    (error && error.message) ||
    "Something went wrong.";
  const requestId = error ? requestIdOf(error) : null;
  NotificationManager.error(
    requestId ? `${message} (reference: ${requestId})` : message
  );
};

/**
 * For a caller that handles its own failures — a background lookup, a dialog
 * with its own wording — but must not swallow an expired session: that one
 * still signs out. Returns whether it did.
 */
export const signOutIfExpired = ({ error, history }) => {
  if (!isSessionExpired(error)) {
    return false;
  }
  handleError({ error, history });
  return true;
};
